import { takeEvery } from 'redux-saga'
import { call, put } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import fetcher from 'modules/fetcher'
import { calculateDurationUnit, convertToDays } from './utils'

import {
  projectTemplatesFetchSuccess,
  projectTemplatesFetchFailure,
  projectTemplateCreate,
  projectTemplateCreateFailure,
  projectTemplateDeleteSuccess,
  projectTemplateDeleteFailure,
  projectTemplateUpdate,
  projectTemplateUpdateFailure,
  projectTemplateToggleEnableSuccess,
  projectTemplateToggleEnableFailure
} from './actions'

import {
  PROJECT_TEMPLATES_FETCH,
  PROJECT_TEMPLATE_CREATE_SUBMIT,
  PROJECT_TEMPLATE_DELETE,
  PROJECT_TEMPLATE_UPDATE_SUBMIT,
  PROJECT_TEMPLATE_TOGGLE_ENABLE
} from './constants'

// Transform API response to match frontend structure
const transformTemplate = (template) => ({
  id: template.id,
  name: template.name,
  isDefault: template.is_default,
  isEnabled: template.is_enabled,
  enabled: template.is_enabled,
  isSample: template.is_sample,
  accountId: template.account_id,
  ownerId: template.owner_id,
  duration: template.duration,
  description: template.description,
  dealOwnerIsCollaborator: template.deal_owner_is_collaborator,
  companyOwnerIsCollaborator: template.company_owner_is_collaborator,
  personOwnerIsCollaborator: template.person_owner_is_collaborator,
  collaborators: (template.collaborators || []).map(collab => ({
    id: collab.id,
    collaboratorType: collab.collaborator_type,
    collaboratorId: collab.collaborator_id
  })),
  milestones: (template.milestones || []).map(milestone => {
    const { value, unit } = calculateDurationUnit(milestone.duration)
    return {
      id: milestone.id,
      name: milestone.name,
      dueDate: value,
      dueDateUnit: unit,
      status: milestone.status,
      description: milestone.description,
      tasks: (milestone.calendar_entries || []).map(entry => ({
        id: entry.id,
        name: entry.name,
        description: entry.description,
        type: entry.calendar_entry_type,
        categoryId: entry.calendar_entry_category_id,
        priorityId: entry.calendar_entry_priority_id,
        statusId: entry.calendar_entry_status_id,
        day: entry.day || 0,
        ownerId: entry.owner_id
      }))
    }
  })
})

// Transform frontend data to Rails format
const transformToRailsFormat = (formData) => {
  const payload = {
    name: formData.name,
    description: formData.description || '',
    duration: convertToDays(formData.duration, formData.durationUnit),
    owner_id: formData.owner_id || null,
    is_enabled: formData.is_enabled || false,
    is_default: formData.is_default || false,
    deal_owner_is_collaborator: formData.deal_owner_is_collaborator || false,
    company_owner_is_collaborator: formData.company_owner_is_collaborator || false,
    person_owner_is_collaborator: formData.person_owner_is_collaborator || false
  }

  if (formData.milestones && formData.milestones.length > 0) {
    payload.milestones_attributes = formData.milestones.map((milestone) => {
      const milestoneData = {
        name: milestone.name,
        duration: convertToDays(milestone.dueDate, milestone.dueDateUnit),
        status: milestone.status || 'not_started',
        description: milestone.description || '',
        calendar_entries_attributes: (milestone.tasks || []).map(task => {
          const taskData = {
            name: task.name,
            description: task.description || '',
            calendar_entry_type: task.type,
            calendar_entry_category_id: task.categoryId || null,
            calendar_entry_priority_id: task.priorityId || null,
            calendar_entry_status_id: task.statusId || null,
            day: task.day || 0,
            owner_id: task.ownerId || null
          }
          // Include id for existing tasks (from database, not Date.now() timestamps)
          // Database IDs are small numbers, timestamps are large (> 1000000000000)
          if (task.id && typeof task.id === 'number' && task.id < 1000000000000) {
            taskData.id = task.id
          }
          // Include _destroy flag if task is marked for deletion
          if (task._destroy) {
            taskData._destroy = true
          }
          return taskData
        })
      }

      // Include id for existing milestones (from database, not Date.now() timestamps)
      // Database IDs are small numbers, timestamps are large (> 1000000000000)
      if (milestone.id && typeof milestone.id === 'number' && milestone.id < 1000000000000) {
        milestoneData.id = milestone.id
      }

      // Include _destroy flag if milestone is marked for deletion
      if (milestone._destroy) {
        milestoneData._destroy = true
      }

      return milestoneData
    })
  }

  if (formData.collaborators && formData.collaborators.length > 0) {
    payload.collaborators_attributes = formData.collaborators.map((collaborator) => {
      const collabData = {
        collaborator_type: collaborator.collaboratorType,
        collaborator_id: collaborator.collaboratorId
      }

      // Include id for existing collaborators
      if (collaborator.id) {
        collabData.id = collaborator.id
      }

      // Include _destroy flag if collaborator is marked for deletion
      if (collaborator._destroy) {
        collabData._destroy = true
      }

      return collabData
    })
  }

  return payload
}

export function * fetchProjectTemplatesSaga () {
  try {
    const response = yield call(fetcher, '/api/v3/admin/project_templates')
    const templates = response.entries.map(transformTemplate)
    yield put(projectTemplatesFetchSuccess(templates))
  } catch (e) {
    yield put(projectTemplatesFetchFailure(e.message))
  }
}

export function * createProjectTemplateSaga ({ payload }) {
  try {
    const railsPayload = transformToRailsFormat(payload)
    const response = yield call(
      fetcher,
      '/api/v3/admin/project_templates',
      {
        method: 'POST',
        body: JSON.stringify({ project_template: railsPayload })
      }
    )
    yield call(window.Helpers.notify, 'Project template created successfully')
    yield put(projectTemplateCreate(transformTemplate(response)))
    yield put(push('/admin/modern/projects/templates'))
  } catch (exception) {
    yield call(window.Helpers.notify, `Error: ${exception.message}`, 'error')
    yield put(projectTemplateCreateFailure(exception.message))
  }
}

export function * deleteProjectTemplateSaga ({ payload }) {
  try {
    yield call(
      fetcher,
      `/api/v3/admin/project_templates/${payload.id}`,
      {
        method: 'DELETE'
      }
    )
    yield call(window.Helpers.notify, 'Project template deleted successfully')
    yield put(projectTemplateDeleteSuccess(payload.id))
    // Refresh the list
    yield put(projectTemplatesFetchSuccess([]))
    yield call(fetchProjectTemplatesSaga)
  } catch (exception) {
    yield call(window.Helpers.notify, `Error: ${exception.message}`, 'error')
    yield put(projectTemplateDeleteFailure(exception.message))
  }
}

export function * updateProjectTemplateSaga ({ payload }) {
  try {
    const railsPayload = transformToRailsFormat(payload.formData)
    const response = yield call(
      fetcher,
      `/api/v3/admin/project_templates/${payload.id}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ project_template: railsPayload })
      }
    )
    yield call(window.Helpers.notify, 'Project template updated successfully')
    yield put(projectTemplateUpdate(transformTemplate(response)))
    yield put(push('/admin/modern/projects/templates'))
  } catch (exception) {
    yield call(window.Helpers.notify, `Error: ${exception.message}`, 'error')
    yield put(projectTemplateUpdateFailure(exception.message))
  }
}

export function * watchFetchProjectTemplatesSaga () {
  yield call(takeEvery, PROJECT_TEMPLATES_FETCH, fetchProjectTemplatesSaga)
}

export function * watchCreateProjectTemplateSaga () {
  yield call(takeEvery, PROJECT_TEMPLATE_CREATE_SUBMIT, createProjectTemplateSaga)
}

export function * watchUpdateProjectTemplateSaga () {
  yield call(takeEvery, PROJECT_TEMPLATE_UPDATE_SUBMIT, updateProjectTemplateSaga)
}

export function * watchDeleteProjectTemplateSaga () {
  yield call(takeEvery, PROJECT_TEMPLATE_DELETE, deleteProjectTemplateSaga)
}

export function * toggleEnableProjectTemplateSaga ({ payload }) {
  try {
    const response = yield call(
      fetcher,
      `/api/v3/admin/project_templates/${payload.id}`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          project_template: {
            is_enabled: payload.isEnabled
          }
        })
      }
    )
    yield put(projectTemplateToggleEnableSuccess(transformTemplate(response)))
  } catch (exception) {
    yield call(window.Helpers.notify, `Error: ${exception.message}`, 'error')
    yield put(projectTemplateToggleEnableFailure(exception.message))
  }
}

export function * watchToggleEnableProjectTemplateSaga () {
  yield call(takeEvery, PROJECT_TEMPLATE_TOGGLE_ENABLE, toggleEnableProjectTemplateSaga)
}
